import React from 'react'
import ScrollToVersion from './ScrollToVersion'
import { ChangelogFilter as FilterType } from '../types'

interface VersionInfo {
  version: string
  slug: string
}

interface ChangelogFilterProps {
  versions: VersionInfo[]
  filter: FilterType
  searchTerm: string
  onFilterChange: (filterKey: keyof FilterType) => void
  onSearchChange: (term: string) => void
  showScrollToVersion?: boolean
  showAdminFilter?: boolean
}

const ChangelogFilter: React.FC<ChangelogFilterProps> = ({
  versions,
  filter,
  searchTerm,
  onFilterChange,
  onSearchChange,
  showScrollToVersion = true,
  showAdminFilter = false,
}) => {
  // Generate stable IDs for accessibility
  const searchInputId = 'changelog-search-input'
  const featureCheckboxId = 'changelog-filter-feature'
  const improvementCheckboxId = 'changelog-filter-improvement'
  const fixCheckboxId = 'changelog-filter-fix'
  const adminCheckboxId = 'changelog-filter-admin'

  const handleFilterChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const filterKey = e.target.value as keyof FilterType
    onFilterChange(filterKey)
  }

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onSearchChange(e.target.value)
  }

  return (
    <div className="ChangelogFilter mb-6">
      <div
        className="changelog-filters p-4 rounded border"
        style={{
          backgroundColor: 'var(--changelog-filter-bg)',
          borderColor: 'var(--changelog-border)',
          borderWidth: '1px',
          borderStyle: 'solid',
        }}
      >
        {/* Top row: Search input */}
        <div className="mb-4">
          <input
            id={searchInputId}
            type="text"
            className="changelog-input js-changelog-input w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 transition-all duration-200"
            style={{
              backgroundColor: 'var(--changelog-input-bg)',
              borderColor: 'var(--changelog-input-border)',
              color: 'var(--changelog-text)',
              fontSize: '14px',
              minHeight: '40px',
            }}
            onFocus={(e) => {
              e.target.style.borderColor = 'var(--changelog-input-focus)'
              e.target.style.boxShadow = `0 0 0 2px var(--changelog-input-focus)25`
            }}
            onBlur={(e) => {
              e.target.style.borderColor = 'var(--changelog-input-border)'
              e.target.style.boxShadow = 'none'
            }}
            placeholder="Search changelog..."
            value={searchTerm}
            onChange={handleSearchChange}
          />
        </div>

        {/* Bottom row: Filters and ScrollToVersion */}
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-6">
            <label
              htmlFor={featureCheckboxId}
              className="flex items-center cursor-pointer group"
            >
              <input
                id={featureCheckboxId}
                className="js-changelog-checkbox changelog-checkbox mr-3 w-4 h-4 rounded transition-all duration-200"
                type="checkbox"
                value="feature"
                checked={filter.feature}
                onChange={handleFilterChange}
                style={
                  {
                    accentColor: 'var(--changelog-checkbox-checked)',
                  } as React.CSSProperties
                }
              />
              <span
                className="changelog-checkbox-label text-sm font-medium select-none group-hover:opacity-80 transition-opacity duration-200"
                style={{
                  color: 'var(--changelog-text)',
                  minWidth: '65px',
                }}
              >
                Features
              </span>
            </label>

            <label
              htmlFor={improvementCheckboxId}
              className="flex items-center cursor-pointer group"
            >
              <input
                id={improvementCheckboxId}
                className="js-changelog-checkbox changelog-checkbox mr-3 w-4 h-4 rounded transition-all duration-200"
                type="checkbox"
                value="improvement"
                checked={filter.improvement}
                onChange={handleFilterChange}
                style={
                  {
                    accentColor: 'var(--changelog-checkbox-checked)',
                  } as React.CSSProperties
                }
              />
              <span
                className="changelog-checkbox-label text-sm font-medium select-none group-hover:opacity-80 transition-opacity duration-200"
                style={{
                  color: 'var(--changelog-text)',
                  minWidth: '95px',
                }}
              >
                Improvements
              </span>
            </label>

            <label
              htmlFor={fixCheckboxId}
              className="flex items-center cursor-pointer group"
            >
              <input
                id={fixCheckboxId}
                className="js-changelog-checkbox changelog-checkbox mr-3 w-4 h-4 rounded transition-all duration-200"
                type="checkbox"
                value="fix"
                checked={filter.fix}
                onChange={handleFilterChange}
                style={
                  {
                    accentColor: 'var(--changelog-checkbox-checked)',
                  } as React.CSSProperties
                }
              />
              <span
                className="changelog-checkbox-label text-sm font-medium select-none group-hover:opacity-80 transition-opacity duration-200"
                style={{
                  color: 'var(--changelog-text)',
                  minWidth: '45px',
                }}
              >
                Bug Fixes
              </span>
            </label>

            {showAdminFilter && (
              <label
                htmlFor={adminCheckboxId}
                className="flex items-center cursor-pointer group"
              >
                <input
                  id={adminCheckboxId}
                  className="js-changelog-checkbox changelog-checkbox mr-3 w-4 h-4 rounded transition-all duration-200"
                  type="checkbox"
                  value="admin"
                  checked={filter.admin}
                  onChange={handleFilterChange}
                  style={
                    {
                      accentColor: 'var(--changelog-checkbox-checked)',
                    } as React.CSSProperties
                  }
                />
                <span
                  className="changelog-checkbox-label text-sm font-medium select-none group-hover:opacity-80 transition-opacity duration-200"
                  style={{
                    color: 'var(--changelog-text)',
                    minWidth: '75px',
                  }}
                >
                  Admin Only
                </span>
              </label>
            )}
          </div>

          {showScrollToVersion && (
            <div className="flex-shrink-0">
              <ScrollToVersion versions={versions} />
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default React.memo(ChangelogFilter)
